import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  DataTable,
  DataTableAddRow,
  DataTableCellContent,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  EmptyState,
  Icon,
  IconTile,
  Image,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  createColumnHelper,
  type DataTableColumnDef,
  type DataTableGroupType,
} from "@salestrak/ui";

import { useFormBannerUrl } from "#app/components/forms/form-banner";
import { DateText } from "#app/components/date-text";
import { EntityTag } from "#app/components/entity";
import { useDeleteSubmission } from "#app/components/forms/form-fill/delete-submission";
import { FormPickerMenu } from "#app/components/forms/form-fill/form-picker-menu";
import { SubmissionShareDialog } from "#app/components/forms/form-fill/submission-share-dialog";
import { SubmissionStatusBadge } from "#app/components/forms/form-fill/submission-status-badge";
import { FormActionTile } from "#app/components/forms/form-tile";
import { FormsViewModeToggle, useFormsViewMode } from "#app/components/forms/forms-view-mode";
import { useMemberEntityResolver } from "#app/lib/data";
import type { IssueRow } from "#app/lib/data/entities/rows";
import { useFormFillDialogActions } from "#app/lib/form-fill-dialog-context";
import { useLocalGroupPages } from "#app/lib/hooks/use-local-group-pages";
import {
  byNewestActivity,
  formVisibility,
  submissionActivityAt,
  useFormsStore,
  type Form,
  type FormSubmission,
} from "#app/lib/forms";
import { countOf } from "#app/lib/utils/pluralize";

import { useIssueViewCapabilities } from "./issue-view-capabilities";

type SubmissionOnIssue = {
  submission: FormSubmission;
  form: Pick<Form, "name" | "bannerUrl" | "bannerMediaId" | "settings">;
  canOpen: boolean;
};

const PINNED_COLUMN_IDS = ["form"];

const h = createColumnHelper<SubmissionOnIssue>();

const DELETED_FORM: SubmissionOnIssue["form"] = { bannerMediaId: null, bannerUrl: null, name: "Deleted form" };

function PrivateBadge({ locked }: { locked: boolean }) {
  const badge = (
    <Badge tone="outline">
      <Icon name="lock" size="xs" />
      Private
    </Badge>
  );
  if (!locked) return badge;
  return (
    <Tooltip>
      <TooltipTrigger render={<Box direction="row" />}>{badge}</TooltipTrigger>
      <TooltipPopup>Private. Only the people it's shared with can open it.</TooltipPopup>
    </Tooltip>
  );
}

function latestBy(latest: FormSubmission): string {
  return latest.submittedBy ?? latest.createdBy;
}

function SubmissionCard({
  row,
  onOpen,
  menu,
}: {
  row: SubmissionOnIssue;
  onOpen: () => void;
  menu: ReactNode;
}) {
  const memberEntity = useMemberEntityResolver();
  const { form, submission, canOpen } = row;
  const title = form.name || "Untitled form";
  const byId = latestBy(submission);
  const banner = useFormBannerUrl(form, "thumb");
  return (
    <Card
      aria-label={title}
      align="center"
      direction="row"
      gap="sm"
      group="card"
      padding="md"
      radius="lg"
      {...(canOpen
        ? { cursor: "pointer", hoverBackground: true, onClick: onOpen, pressFeedback: true, role: "link" }
        : {})}
    >
      {banner ? (
        <Box height={32} overflow="hidden" rounded="lg" shrink={false} width={32}>
          <Image alt="" fill fit="cover" src={banner} />
        </Box>
      ) : (
        <IconTile color="muted" name="clipboard-check" size="lg" />
      )}
      <Box direction="column" fill="flex" gap="2xs" minWidth={0}>
        <Text size="sm" truncate weight="semibold">
          {title}
        </Text>
        <Box align="center" direction="row" gap="xs" minWidth={0}>
          <EntityTag
            entity={memberEntity(byId) ?? { id: byId, kind: "user", user: null }}
            interactive={false}
            size="xs"
            weight="regular"
          />
          <Box direction="row" shrink={false}>
            <Text size="xs" variant="faint">
              ·&nbsp;
            </Text>
            <DateText date={submissionActivityAt(submission)} size="xs" truncate={false} variant="faint" />
          </Box>
        </Box>
      </Box>
      {/* Stops presses here: React bubbles through the portalled popup and would open the card. */}
      <Box align="center" direction="row" gap="xs" onClick={(event) => event.stopPropagation()} shrink={false}>
        {!canOpen ? (
          <PrivateBadge locked />
        ) : submission.status === "inProgress" ? (
          <SubmissionStatusBadge status="inProgress" />
        ) : null}
        {menu ? <Box hoverReveal="card">{menu}</Box> : null}
      </Box>
    </Card>
  );
}

export function IssueFormsTab({ issue }: { issue: IssueRow }) {
  const store = useFormsStore(issue.issueId);
  const { open } = useFormFillDialogActions();
  const memberEntity = useMemberEntityResolver();
  const { mode, setMode } = useFormsViewMode("project");
  const { canEdit } = useIssueViewCapabilities();
  const rows = useMemo<SubmissionOnIssue[]>(
    () =>
      [...store.submissions]
        .sort(byNewestActivity)
        .map((submission) => ({
          canOpen: submission.viewerCanOpen,
          form: store.formsById.get(submission.formId) ?? DELETED_FORM,
          submission,
        })),
    [store.formsById, store.submissions],
  );

  const [sharingId, setSharingId] = useState<string | null>(null);
  const sharing = sharingId
    ? (store.submissions.find((row) => row.submissionId === sharingId) ?? null)
    : null;
  const openRow = useCallback(
    ({ submission }: SubmissionOnIssue) =>
      open({ issueId: submission.issueId, kind: "submission", submissionId: submission.submissionId }),
    [open],
  );

  const canDelete = (row: SubmissionOnIssue) => row.submission.viewerCanDelete;
  const { request: requestDelete, confirm: deleteConfirm } = useDeleteSubmission(store);
  // Public reaches everyone who sees the Project, so only Private forms share.
  const canShare = (row: SubmissionOnIssue) =>
    row.canOpen && formVisibility(row.form) === "private";
  const shareItem = (row: SubmissionOnIssue) =>
    canShare(row) ? (
      <MenuItem onClick={() => setSharingId(row.submission.submissionId)}>
        <Icon color="muted" name="share" size="md" />
        <Text size="sm">Share</Text>
      </MenuItem>
    ) : null;
  const deleteItem = (row: SubmissionOnIssue, below: boolean) =>
    canDelete(row) ? (
      <>
        {below ? <MenuSeparator /> : null}
        <MenuItem onClick={() => requestDelete(row.submission)} variant="destructive">
          <Icon name="trash" size="md" />
          <Text size="sm">Delete</Text>
        </MenuItem>
      </>
    ) : null;

  const columns = useMemo<DataTableColumnDef<SubmissionOnIssue>[]>(
    () => [
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size={null} truncate weight="medium">
              {row.original.form.name || "Untitled form"}
            </Text>
          </DataTableCellContent>
        ),
        header: "Form",
        id: "form",
        meta: { minWidth: 240 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            {row.original.canOpen && formVisibility(row.original.form) === "public" ? (
              <Text size={null} variant="muted">
                —
              </Text>
            ) : (
              <PrivateBadge locked={!row.original.canOpen} />
            )}
          </DataTableCellContent>
        ),
        header: "Access",
        id: "access",
        meta: { width: 120 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <SubmissionStatusBadge status={row.original.submission.status} />
          </DataTableCellContent>
        ),
        header: "Status",
        id: "status",
        meta: { width: 160 },
      }),
      h.display({
        cell: ({ row }) => {
          const byId = latestBy(row.original.submission);
          return (
            <DataTableCellContent>
              <EntityTag
                entity={memberEntity(byId) ?? { id: byId, kind: "user", user: null }}
                interactive={false}
                preview
                weight="regular"
              />
            </DataTableCellContent>
          );
        },
        header: "Filled by",
        id: "by",
        meta: { width: 200 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <DateText date={submissionActivityAt(row.original.submission)} />
          </DataTableCellContent>
        ),
        header: "Updated",
        id: "updated",
        meta: { width: 160 },
      }),
    ],
    [memberEntity],
  );

  const groupState = useLocalGroupPages(rows, `forms:${issue.issueId}`);
  const groups = useMemo<DataTableGroupType<SubmissionOnIssue>[]>(() => {
    const { total, ...groupProps } = groupState;
    return [{ key: "__all__", label: "All Submissions", totalCount: total, ...groupProps }];
  }, [groupState]);

  const groupFooter = useCallback(
    () => (
      <FormPickerMenu
        issue={issue}
        trigger={<DataTableAddRow aria-label="Add form" label="Add form" />}
      />
    ),
    [issue],
  );

  if (store.formsLoading || store.submissionsLoading) return null;
  if (store.formsFailed) {
    return (
      <Box align="center" fill="flex" justify="center" padding="xl">
        <EmptyState
          actions={
            <Button onClick={store.refetchForms} size="sm" variant="outline">
              <Text size="sm">Try again</Text>
            </Button>
          }
          description="Check your connection and try again."
          icon="clipboard-check"
          media="icon"
          title="Couldn't load forms"
        />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box align="center" fill="flex" justify="center" padding="xl">
        <EmptyState
          {...(canEdit
            ? {
                actions: (
                  <FormPickerMenu
                    issue={issue}
                    trigger={
                      <Button size="sm">
                        <Icon name="plus" size="md" />
                        <Text size="sm">Add form</Text>
                      </Button>
                    }
                  />
                ),
              }
            : {})}
          description={
            canEdit
              ? "Pick a form to fill out on this Project."
              : "Forms filled out on this Project show up here."
          }
          icon="clipboard-check"
          media="icon"
          title="No forms yet"
        />
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <DataTableToolbar>
        <DataTableToolbarLeft>
          <Text size="sm" variant="muted">
            {countOf(rows.length, "submission")}
          </Text>
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <FormsViewModeToggle mode={mode} onModeChange={setMode} />
        </DataTableToolbarActions>
      </DataTableToolbar>
      {mode === "table" ? (
        <DataTable
          pinnedColumnIds={PINNED_COLUMN_IDS}
          columns={columns}
          density="regular"
          getRowId={(row) => row.submission.submissionId}
          {...(canEdit ? { groupFooter } : {})}
          groups={groups}
          onRowClick={(row) => openRow(row.original)}
          paddingX="lg"
          renderRowActions={(row) =>
            row.canOpen ? (
              <>
                <MenuItem onClick={() => openRow(row)}>
                  <Icon color="muted" name="external-link" size="md" />
                  <Text size="sm">Open</Text>
                </MenuItem>
                {shareItem(row)}
                {deleteItem(row, true)}
              </>
            ) : (
              deleteItem(row, false)
            )
          }
          rowClickable={(row) => row.original.canOpen}
          stickyHeader
        />
      ) : (
        // Flex-determined size, so size containment is safe for the gutter measure.
        <Box className="[container-type:size]" fill="flex" minHeight={0} overflow="auto">
          <Box
            aria-label="Submissions"
            columns={{ base: 1, sm: 2, xl: 3 }}
            gap="sm"
            paddingBottom="lg"
            paddingX="lg"
            role="list"
          >
            {rows.map((row) => (
              <Box key={row.submission.submissionId} role="listitem">
                <SubmissionCard
                  menu={
                    canShare(row) || canDelete(row) ? (
                      <Menu>
                        <MenuTrigger
                          render={
                            <Button aria-label={`Actions for ${row.form.name || "Untitled form"}`} size="icon-xs" variant="ghost">
                              <Icon name="ellipsis" size="md" />
                            </Button>
                          }
                        />
                        <MenuPopup align="end">
                          {shareItem(row)}
                          {deleteItem(row, canShare(row))}
                        </MenuPopup>
                      </Menu>
                    ) : null
                  }
                  onOpen={() => openRow(row)}
                  row={row}
                />
              </Box>
            ))}
            {canEdit ? (
              <Box role="listitem">
                <FormPickerMenu
                  issue={issue}
                  trigger={<FormActionTile direction="row" label="Add form" minHeight={0} />}
                />
              </Box>
            ) : null}
          </Box>
          <Box aria-hidden height="50cqh" shrink={false} />
        </Box>
      )}
      {deleteConfirm}
      <SubmissionShareDialog
        canRemove={sharing?.viewerCanRemoveShares ?? false}
        formName={sharing ? (store.formsById.get(sharing.formId)?.name ?? "") : ""}
        onChange={store.setSubmissionShares}
        onOpenChange={(open) => (open ? undefined : setSharingId(null))}
        submission={sharing}
      />
    </Box>
  );
}
